import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, Eye, FileText, Mail, MapPin, Phone, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/shared/SEO';

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "September 19, 2026";

  const sections = [
    { id: "introduction", title: "1. Introduction & Scope" },
    { id: "information-collected", title: "2. Information We Collect" },
    { id: "usage", title: "3. How We Use Your Information" },
    { id: "protection", title: "4. Data Protection & Security" },
    { id: "cookies-analytics", title: "5. Cookies & Website Analytics" },
    { id: "disclosure", title: "6. Third-Party Disclosures" },
    { id: "rights", title: "7. Your Rights & Data Retention" },
    { id: "contact", title: "8. Grievance Officer & Contact" },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy | Shubha Lakshmi Enterprises"
        description="Read the Privacy Policy of Shubha Lakshmi Enterprises. Learn how we collect, use, protect, and handle business information and quotation inquiries for hospitality clients."
      />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-36 pb-16 bg-gradient-to-b from-stone-100 to-[#FAFAF9] border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#D4AF37] font-semibold mb-3">
            <ShieldCheck className="w-4 h-4 text-[#14532D]" />
            Trust, Transparency & Compliance
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
            Privacy Policy
          </h1>
          <p className="text-gray-600 max-w-2xl text-base leading-relaxed">
            At Shubha Lakshmi Enterprises, we respect your business privacy and are committed to safeguarding
            the proprietary and personal data of our hospitality partners, clients, and website visitors.
          </p>
          <div className="mt-6 flex items-center gap-4 text-xs text-gray-500 font-medium">
            <span>Effective Date: {lastUpdated}</span>
            <span>•</span>
            <span>Applicable to: www.sleonline.in</span>
          </div>
        </div>
      </section>

      {/* Main Content Layout */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">

          {/* Table of Contents Sidebar */}
          <aside className="md:col-span-4">
            <div className="sticky top-28 bg-white p-6 rounded-2xl border border-stone-200 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-4">
                Contents
              </h3>
              <nav className="flex flex-col gap-2.5">
                {sections.map((sec) => (
                  <a
                    key={sec.id}
                    href={`#${sec.id}`}
                    className="text-sm text-gray-600 hover:text-[#14532D] hover:font-medium transition-colors"
                  >
                    {sec.title}
                  </a>
                ))}
              </nav>

              <hr className="my-6 border-stone-100" />

              <div className="bg-stone-50 p-4 rounded-xl border border-stone-200/60">
                <p className="text-xs font-semibold text-gray-800 mb-1">Have questions?</p>
                <p className="text-xs text-gray-500 mb-3">Our team is available to assist with data inquiries.</p>
                <a
                  href="mailto:contact@sleonline.in"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#14532D] hover:underline"
                >
                  <Mail className="w-3.5 h-3.5" /> contact@sleonline.in
                </a>
              </div>
            </div>
          </aside>

          {/* Policy Text Articles */}
          <main className="md:col-span-8 bg-white p-8 md:p-12 rounded-2xl border border-stone-200 shadow-xs space-y-12">

            {/* Section 1 */}
            <article id="introduction" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                1. Introduction & Scope
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                <strong>Shubha Lakshmi Enterprises</strong> (&quot;SLE&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates
                <a href="https://www.sleonline.in" className="text-[#14532D] underline font-medium mx-1">www.sleonline.in</a>
                as a premier B2B hospitality product catalogue and quotation platform based in Jaipur, Rajasthan, India.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                This Privacy Policy outlines our procedures regarding the collection, storage, use, and protection
                of commercial and personal information provided when you browse our catalogue, request samples,
                submit inquiries, or request customized hospitality amenity quotations.
              </p>
            </article>

            {/* Section 2 */}
            <article id="information-collected" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                2. Information We Collect
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Because we cater strictly to commercial clients (hotels, resorts, restaurants, event caterers, and institutions),
                we collect only business-necessary details:
              </p>
              <div className="space-y-3 mb-4">
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <strong>Commercial & Contact Information:</strong> Name of procurement contact, business email address, corporate phone number, property/hotel name, delivery address, and GSTIN/Tax identification.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <strong>Inquiry & Product Specifications:</strong> Product SKUs of interest, required quantities, custom branding specifications, room package bundles, and timeline requirements.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-gray-700 leading-relaxed">
                    <strong>Technical & Browsing Telemetry:</strong> Anonymized interaction metrics (page navigation paths, catalogue downloads, search terms, and approximate device category) used solely for operational website performance.
                  </p>
                </div>
              </div>
            </article>

            {/* Section 3 */}
            <article id="usage" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                3. How We Use Your Information
              </h2>
              <ul className="list-disc list-inside space-y-2 text-sm text-gray-600 leading-relaxed mb-4">
                <li>Generating customized, legally valid PDF quotations and formal B2B pricing estimates.</li>
                <li>Dispatching physical product samples and amenity testing kits to your hotel or corporate office.</li>
                <li>Fulfilling and tracking logistics, delivery schedules, and commercial wholesale contracts.</li>
                <li>Communicating updates, inventory availability, and order confirmations via phone or verified email.</li>
                <li>Enhancing our catalogue discovery through privacy-preserving semantic search indexing.</li>
              </ul>
              <p className="text-gray-600 text-sm leading-relaxed">
                We <strong>never</strong> sell, rent, monetize, or trade your contact or procurement data to advertisers, data brokers, or third-party marketing agencies.
              </p>
            </article>

            {/* Section 4 */}
            <article id="protection" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                4. Data Protection & Security
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We maintain rigorous technical and administrative safeguards to protect your records:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                  <Lock className="w-5 h-5 text-[#14532D] mb-2" />
                  <h4 className="text-xs font-bold text-gray-900 mb-1">Encrypted In-Transit & At-Rest</h4>
                  <p className="text-xs text-gray-500">All data transferred between your browser and our servers is secured using modern TLS/HTTPS encryption.</p>
                </div>
                <div className="p-4 rounded-xl bg-stone-50 border border-stone-200/80">
                  <Eye className="w-5 h-5 text-[#14532D] mb-2" />
                  <h4 className="text-xs font-bold text-gray-900 mb-1">Strict JWT Role-Based Access</h4>
                  <p className="text-xs text-gray-500">Administrative tools and quotation databases are restricted behind JWT authentication accessible only by authorized personnel.</p>
                </div>
              </div>
            </article>

            {/* Section 5 */}
            <article id="cookies-analytics" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                5. Cookies & Website Analytics
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                Our website utilizes lightweight session tokens and anonymized analytics to ensure optimal functionality:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600 leading-relaxed mb-3">
                <li><strong>Essential Functional Cookies:</strong> Session tokens used exclusively for administrative login security and CSRF protection.</li>
                <li><strong>Privacy-Preserving Telemetry:</strong> Aggregate page view counters and search query aggregations that contain zero personally identifiable information (PII).</li>
              </ul>
              <p className="text-gray-600 text-sm leading-relaxed">
                We do not employ cross-site tracking cookies, behavioral ad-targeting pixels, or invasive surveillance scripts.
              </p>
            </article>

            {/* Section 6 */}
            <article id="disclosure" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                6. Third-Party Disclosures
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                Information is only shared under the following strictly necessary circumstances:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600 leading-relaxed">
                <li><strong>Logistics & Freight Partners:</strong> Delivery address and recipient contact details shared with trusted courier partners to transport bulk amenities.</li>
                <li><strong>Legal & Statutory Obligations:</strong> Compliance with applicable Indian laws, tax reporting (GST filings), or lawful judicial orders.</li>
              </ul>
            </article>

            {/* Section 7 */}
            <article id="rights" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                7. Your Rights & Data Retention
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-3">
                As a client or business representative, you hold the right to:
              </p>
              <ul className="list-disc list-inside space-y-1.5 text-sm text-gray-600 leading-relaxed mb-4">
                <li>Request a summary of quotations and correspondence linked to your corporate profile.</li>
                <li>Rectify or update corporate contact details or tax information.</li>
                <li>Request deletion or anonymization of past inquiries (subject to mandatory tax and accounting record-keeping laws).</li>
              </ul>
            </article>

            {/* Section 8 */}
            <article id="contact" className="scroll-mt-32">
              <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Manrope' }}>
                8. Grievance Officer & Contact
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                For questions, clarifications, or requests regarding this policy, please reach our administrative office:
              </p>
              <div className="bg-stone-50 p-6 rounded-xl border border-stone-200 text-sm space-y-3">
                <div className="flex items-center gap-3 text-gray-700">
                  <ShieldCheck className="w-5 h-5 text-[#D4AF37] shrink-0" />
                  <strong>Shubha Lakshmi Enterprises</strong>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <MapPin className="w-5 h-5 text-[#14532D] shrink-0" />
                  <span>Jaipur, Rajasthan, India</span>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail className="w-5 h-5 text-[#14532D] shrink-0" />
                  <a href="mailto:info@sleonline.in" className="text-[#14532D] hover:underline font-medium">info@sleonline.in</a>
                </div>
                <div className="flex items-center gap-3 text-gray-600">
                  <Phone className="w-5 h-5 text-[#14532D] shrink-0" />
                  <span>+91 (0) 141 - Inquiries Line</span>
                </div>
              </div>
            </article>

            <div className="pt-6 border-t border-stone-200 flex justify-between items-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-xs font-semibold text-gray-500 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" /> Return to Home
              </Link>
              <span className="text-xs text-gray-400">
                © {new Date().getFullYear()} Shubha Lakshmi Enterprises
              </span>
            </div>

          </main>
        </div>
      </section>

      <Footer />
    </div>
  );
}
